import React, { useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import moment from 'moment';
import { useGraphData } from '../../hooks/useMeters';
import { buildWidgetConfig } from '../../services/meterApi';
import { Period, comparePeriod } from '../../utils/consumptionPeriods';
import StatCard from './StatCard';

/**
 * Reads the widget response, whichever shape it arrives in.
 * @param {object} payload - Response from the graph endpoint.
 * @returns {Array<object>} The rows, empty when there are none.
 */
const rowsOf = (payload) => {
  const rows = payload?.data?.data ?? payload?.data ?? [];
  return Array.isArray(rows) ? rows : [];
};

/**
 * Consumption for today, this week and this month, each against the same
 * elapsed span of the period before it.
 *
 * Both sides of every comparison are summed from fine-grained buckets rather
 * than read from pre-aggregated week and month totals. A whole-period bucket
 * can only be set against another whole period, which is what made the month
 * card report an 84% collapse on the fifth of the month.
 *
 * @param {object} meter - Meter as returned by the meters API.
 * @param {string} currentFY - Financial year the figures are scoped to.
 * @param {object} currentFYKpi - The meter's KPI mapping for that year.
 */
const QuickStatsSection = ({ meter, currentFY, currentFYKpi }) => {
  const configs = useMemo(() => {
    if (!meter || !currentFYKpi) return null;

    const base = {
      meterId: meter.meterId,
      financialYear: currentFY,
      locationId: meter.sourceId?.toString() || '1',
      module: currentFYKpi.module,
      category: currentFYKpi.category,
      subCategory: currentFYKpi.subCategory,
      kpi: currentFYKpi.kpi,
      kpiType: 'EXTERNAL',
      xAxisField: 'displayPeriods',
      aggregationPeriodValue: 1,
    };

    return {
      hourly: buildWidgetConfig({
        ...base,
        aggregationPeriodUnit: 'hours',
        startDateTime: moment().subtract(2, 'days').startOf('day').toISOString(),
        endDateTime: moment().toISOString(),
      }),
      daily: buildWidgetConfig({
        ...base,
        aggregationPeriodUnit: 'days',
        startDateTime: moment().subtract(1, 'month').startOf('month').toISOString(),
        endDateTime: moment().toISOString(),
      }),
    };
  }, [meter, currentFY, currentFYKpi]);

  const { data: hourlyData, isLoading: hourlyLoading } = useGraphData(
    configs?.hourly,
    { enabled: !!configs }
  );
  const { data: dailyData, isLoading: dailyLoading } = useGraphData(
    configs?.daily,
    { enabled: !!configs }
  );

  const cards = useMemo(() => {
    const hourly = rowsOf(hourlyData);
    const daily = rowsOf(dailyData);

    return [
      { title: 'Today', stats: comparePeriod(hourly, Period.DAY) },
      { title: 'This Week', stats: comparePeriod(daily, Period.WEEK) },
      { title: 'This Month', stats: comparePeriod(daily, Period.MONTH) },
    ];
  }, [hourlyData, dailyData]);

  if (!configs || hourlyLoading || dailyLoading) {
    return (
      <div className="quick-stats-section">
        <h2 className="section-title">Consumption</h2>
        <div className="text-center p-4">
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="quick-stats-section">
      <h2 className="section-title">Consumption</h2>
      <div className="quick-stats-grid">
        {cards.map(({ title, stats }) => (
          <StatCard
            key={title}
            title={title}
            unit={meter.unit}
            value={stats.value}
            baseline={stats.baseline}
            comparable={stats.comparable}
            change={stats.change}
            changeValue={stats.changeValue}
            against={stats.against}
            elapsed={stats.elapsed}
          />
        ))}
      </div>
    </div>
  );
};

export default QuickStatsSection;
